import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE, appReleaseAPI } from '../utils/api';

const DEFAULT_RELEASE_FORM = {
  slug: 'gotchat',
  name: 'chatchat',
  repoSlug: 'gotchat',
  description: '',
  latestVersion: '',
  allowedGitEmails: []
};

const emptyForm = {
  ...DEFAULT_RELEASE_FORM
};

function normalizeError(error, fallback) {
  return error?.response?.data?.Message || error?.response?.data?.message || error?.message || fallback;
}

function releaseValue(data, key) {
  return data?.[key] || data?.[key.charAt(0).toUpperCase() + key.slice(1)] || '';
}

function releaseVersions(data) {
  const versions = data?.versions || data?.Versions;
  return Array.isArray(versions) ? versions : [];
}

function releaseEmails(data) {
  const emails = data?.allowedGitEmails || data?.AllowedGitEmails;
  return Array.isArray(emails) ? emails : [];
}

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function AdminAppReleaseManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [gitAccessEmail, setGitAccessEmail] = useState('');
  const [selectedSlug, setSelectedSlug] = useState('');
  const [archiveFile, setArchiveFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState('');
  const [updatingGitAccess, setUpdatingGitAccess] = useState(false);

  const gitBase = useMemo(() => String(API_BASE || '').replace(/\/api\/?$/, ''), []);
  const selectedItem = useMemo(
    () => items.find((item) => releaseValue(item, 'slug') === selectedSlug) || null,
    [items, selectedSlug]
  );
  const selectedVersions = useMemo(() => releaseVersions(selectedItem), [selectedItem]);
  const activeRepoSlug = (form.repoSlug || releaseValue(selectedItem, 'repoSlug') || '').trim();
  const gitRemoteUrl = releaseValue(selectedItem, 'gitRemoteUrl') || (activeRepoSlug ? `${gitBase}/git/${activeRepoSlug}.git` : '');

  const hydrateForm = useCallback((data, fallbackSlug = '') => {
    setForm({
      slug: releaseValue(data, 'slug') || fallbackSlug,
      name: releaseValue(data, 'name') || '',
      repoSlug: releaseValue(data, 'repoSlug') || '',
      description: releaseValue(data, 'description') || '',
      latestVersion: releaseValue(data, 'latestVersion') || '',
      allowedGitEmails: releaseEmails(data)
        .map((value) => normalizeEmail(value))
        .filter(Boolean)
    });
    const slug = releaseValue(data, 'slug') || fallbackSlug;
    if (slug) {
      setSelectedSlug(slug);
    }
  }, []);

  const loadRelease = useCallback(async (slug, showStatus = true) => {
    if (!slug) return;
    if (showStatus) setStatus('Loading release details...');
    try {
      const response = await appReleaseAPI.getBySlug(slug);
      const data = response?.data || {};
      hydrateForm(data, slug);
      if (showStatus) setStatus('Release details loaded.');
    } catch (error) {
      setStatus(normalizeError(error, 'Failed to load release details.'));
    }
  }, [hydrateForm]);

  const loadItems = useCallback(async (preferredSlug = '') => {
    setLoading(true);
    setStatus('');
    try {
      const response = await appReleaseAPI.getAdminList();
      const list = Array.isArray(response?.data) ? response.data : [];
      setItems(list);
      if (preferredSlug) {
        const match = list.find((item) => releaseValue(item, 'slug') === preferredSlug);
        if (match) {
          hydrateForm(match, preferredSlug);
          await loadRelease(preferredSlug, false);
        }
      }
    } catch (error) {
      setStatus(normalizeError(error, 'Failed to load app releases.'));
    } finally {
      setLoading(false);
    }
  }, [hydrateForm, loadRelease]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  function startNew() {
    setSelectedSlug('');
    setArchiveFile(null);
    setGitAccessEmail('');
    setForm({ ...DEFAULT_RELEASE_FORM });
    setStatus('Creating a new app release entry.');
  }

  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function persistGitAccessEmails(nextEmails, successMessage, fallbackMessage, rollbackEmails) {
    const slug = form.slug.trim();
    const repoSlug = form.repoSlug.trim();
    if (!slug || !repoSlug) {
      setStatus(`${successMessage} Save App Release to persist this change.`);
      return;
    }

    setUpdatingGitAccess(true);
    try {
      const payload = {
        slug,
        name: form.name.trim(),
        repoSlug,
        description: form.description.trim(),
        latestVersion: form.latestVersion.trim(),
        allowedGitEmails: nextEmails
      };
      const response = await appReleaseAPI.upsert(payload);
      const saved = response?.data?.Release || response?.data || {};
      const savedSlug = releaseValue(saved, 'slug') || slug;
      hydrateForm(saved, savedSlug);
      setSelectedSlug(savedSlug);
      setStatus(successMessage);
      await loadItems(savedSlug);
      await loadRelease(savedSlug, false);
    } catch (error) {
      setForm((current) => ({
        ...current,
        allowedGitEmails: rollbackEmails
      }));
      setStatus(normalizeError(error, fallbackMessage));
    } finally {
      setUpdatingGitAccess(false);
    }
  }

  async function addGitAccessEmail() {
    const nextEmail = normalizeEmail(gitAccessEmail);
    if (!nextEmail) {
      setStatus('Enter an email address to grant app git access.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setStatus('Enter a valid email address.');
      return;
    }
    const currentEmails = (form.allowedGitEmails || []).map((value) => normalizeEmail(value)).filter(Boolean);
    const nextEmails = Array.from(new Set([...currentEmails, nextEmail])).sort();
    if (nextEmails.length === currentEmails.length) {
      setStatus(`${nextEmail} already has app git access.`);
      return;
    }
    setForm((current) => ({
      ...current,
      allowedGitEmails: nextEmails
    }));
    setGitAccessEmail('');
    await persistGitAccessEmails(
      nextEmails,
      `Added ${nextEmail} to the app git access list.`,
      `Failed to add ${nextEmail} to the app git access list.`,
      currentEmails
    );
  }

  async function removeGitAccessEmail(email) {
    const nextEmail = normalizeEmail(email);
    const currentEmails = (form.allowedGitEmails || []).map((value) => normalizeEmail(value)).filter(Boolean);
    const nextEmails = currentEmails.filter((value) => normalizeEmail(value) !== nextEmail);
    setForm((current) => ({
      ...current,
      allowedGitEmails: nextEmails
    }));
    await persistGitAccessEmails(
      nextEmails,
      `Removed ${nextEmail} from the app git access list.`,
      `Failed to remove ${nextEmail} from the app git access list.`,
      currentEmails
    );
  }

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    setStatus('Saving app release...');
    try {
      const payload = {
        slug: form.slug.trim(),
        name: form.name.trim(),
        repoSlug: form.repoSlug.trim(),
        description: form.description.trim(),
        latestVersion: form.latestVersion.trim(),
        allowedGitEmails: (form.allowedGitEmails || []).map((value) => normalizeEmail(value)).filter(Boolean)
      };
      const response = await appReleaseAPI.upsert(payload);
      const saved = response?.data?.Release || response?.data || {};
      const slug = releaseValue(saved, 'slug') || payload.slug;
      hydrateForm(saved, slug);
      setSelectedSlug(slug);
      setStatus(`Saved ${slug}.`);
      await loadItems(slug);
      await loadRelease(slug, false);
    } catch (error) {
      setStatus(normalizeError(error, 'Failed to save app release.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleArchiveUpload(event) {
    event.preventDefault();
    if (!archiveFile) {
      setStatus('Select a git archive zip first.');
      return;
    }

    setUploading(true);
    setStatus('Uploading app release archive...');
    try {
      const payload = new FormData();
      payload.append('slug', form.slug.trim());
      payload.append('name', form.name.trim());
      payload.append('repoSlug', form.repoSlug.trim());
      payload.append('description', form.description.trim());
      payload.append('latestVersion', form.latestVersion.trim());
      payload.append('gitArchive', archiveFile);

      const response = await appReleaseAPI.upload(payload);
      const saved = response?.data?.Release || response?.data || {};
      const slug = releaseValue(saved, 'slug') || form.slug.trim();
      hydrateForm(saved, slug);
      setSelectedSlug(slug);
      setArchiveFile(null);
      setGitAccessEmail('');
      setStatus(response?.data?.Message || `Uploaded ${slug}.`);
      try {
        await loadItems(slug);
        await loadRelease(slug, false);
      } catch (refreshError) {
        const refreshMessage = normalizeError(refreshError, 'Upload succeeded, but refreshing release details failed.');
        setStatus(`${response?.data?.Message || `Uploaded ${slug}.`} ${refreshMessage}`);
      }
    } catch (error) {
      setStatus(normalizeError(error, 'Failed to upload app release archive.'));
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteSelected() {
    const slug = releaseValue(selectedItem, 'slug') || form.slug.trim();
    if (!slug) {
      setStatus('Select an app release first.');
      return;
    }

    const confirmed = window.confirm(
      `Remove app release '${slug}'?\n\nThis will delete the registered app entry and its backing git repository.`
    );
    if (!confirmed) {
      return;
    }

    setDeleting(true);
    setStatus(`Removing ${slug}...`);
    try {
      const response = await appReleaseAPI.delete(slug);
      setSelectedSlug('');
      setArchiveFile(null);
      setForm({ ...DEFAULT_RELEASE_FORM });
      setStatus(response?.data?.Message || `Removed ${slug}.`);
      await loadItems();
    } catch (error) {
      setStatus(normalizeError(error, 'Failed to remove app release.'));
    } finally {
      setDeleting(false);
    }
  }

  const panelTitle = selectedSlug ? `Edit App Release: ${selectedSlug}` : 'Create App Release';
  const gitCommands = gitRemoteUrl
    ? [`git remote add origin ${gitRemoteUrl}`, 'git add .', 'git commit -m "Update framework"', 'git push -u origin main', 'git tag v1.2.3', 'git push origin v1.2.3']
    : [];

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.heading}>App Releases</h2>
          <p style={styles.subtle}>
            Admin can seed or refresh a framework repo from a git archive zip, then use git pushes and tags for future versioning.
          </p>
        </div>
        <div style={styles.headerActions}>
          <button type="button" style={styles.secondaryButton} onClick={() => loadItems(selectedSlug)} disabled={loading}>
            Refresh List
          </button>
          <button type="button" style={styles.primaryButton} onClick={startNew}>
            New App Release
          </button>
        </div>
      </div>

      <div style={styles.layout}>
        <section style={styles.listPanel}>
          <div style={styles.panelHeader}>Registered Apps</div>
          {loading ? <div style={styles.emptyState}>Loading app releases...</div> : null}
          {!loading && items.length === 0 ? <div style={styles.emptyState}>No app releases registered yet.</div> : null}
          {!loading && items.length > 0 ? (
            <div style={styles.list}>
              {items.map((item) => {
                const slug = releaseValue(item, 'slug');
                const latestVersion = releaseValue(item, 'latestVersion');
                const repoSlug = releaseValue(item, 'repoSlug');
                const updatedAt = releaseValue(item, 'updatedAt');
                return (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => loadRelease(slug)}
                    style={{
                      ...styles.listItem,
                      ...(slug === selectedSlug ? styles.listItemActive : {})
                    }}
                  >
                    <div style={styles.listItemTitle}>{releaseValue(item, 'name') || slug}</div>
                    <div style={styles.listItemMeta}>Slug: {slug}</div>
                    <div style={styles.listItemMeta}>Repo: {repoSlug || '-'}</div>
                    <div style={styles.listItemMeta}>Latest: {latestVersion || 'auto from tag'}</div>
                    <div style={styles.listItemMeta}>Git Access Users: {releaseEmails(item).length}</div>
                    <div style={styles.listItemMeta}>Updated: {updatedAt ? new Date(updatedAt).toLocaleString() : '-'}</div>
                  </button>
                );
              })}
            </div>
          ) : null}
        </section>

        <section style={styles.formPanel}>
          <div style={styles.panelHeader}>{panelTitle}</div>
          <form onSubmit={handleSave} style={styles.form}>
            <label style={styles.label}>
              <span>Slug</span>
              <input
                style={styles.input}
                value={form.slug}
                onChange={(event) => updateField('slug', event.target.value)}
                placeholder={DEFAULT_RELEASE_FORM.slug}
                required
              />
            </label>

            <label style={styles.label}>
              <span>Name</span>
              <input
                style={styles.input}
                value={form.name}
                onChange={(event) => updateField('name', event.target.value)}
                placeholder={DEFAULT_RELEASE_FORM.name}
              />
            </label>

            <label style={styles.label}>
              <span>Repository Slug</span>
              <input
                style={styles.input}
                value={form.repoSlug}
                onChange={(event) => updateField('repoSlug', event.target.value)}
                placeholder={DEFAULT_RELEASE_FORM.repoSlug}
                required
              />
            </label>

            <label style={styles.label}>
              <span>Description</span>
              <textarea
                style={styles.textarea}
                value={form.description}
                onChange={(event) => updateField('description', event.target.value)}
                placeholder="Desktop app release channel"
                rows={4}
              />
            </label>

            <label style={styles.label}>
              <span>Latest Version Override</span>
              <input
                style={styles.input}
                value={form.latestVersion}
                onChange={(event) => updateField('latestVersion', event.target.value)}
                placeholder="Leave blank to resolve from README or git tag"
              />
            </label>

            <div style={styles.noteBox}>
              Save creates or updates the release record. Uploading a git archive seeds or refreshes the backing repo and can also populate version metadata from the README.
            </div>

            <div style={styles.formActions}>
              <button type="submit" style={styles.primaryButton} disabled={saving}>
                {saving ? 'Saving...' : 'Save App Release'}
              </button>
              {selectedItem ? (
                <>
                  <button type="button" style={styles.secondaryButton} onClick={() => loadRelease(selectedSlug)}>
                    Reload Details
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => window.open(releaseValue(selectedItem, 'downloadUrl'), '_blank', 'noopener,noreferrer')}
                  >
                    Download Archive
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => window.open(releaseValue(selectedItem, 'gitLogUrl'), '_blank', 'noopener,noreferrer')}
                  >
                    View Git Log
                  </button>
                  <button
                    type="button"
                    style={styles.dangerButton}
                    onClick={() => void handleDeleteSelected()}
                    disabled={deleting}
                  >
                    {deleting ? 'Removing...' : 'Remove App Release'}
                  </button>
                </>
              ) : null}
            </div>
          </form>

          <div style={styles.gitAccessBox}>
            <div style={styles.panelHeader}>App Git Access</div>
            <div style={styles.noteBox}>
              Add existing Plugin Repository user emails here. Those users can authenticate with their own credentials and access this app's git remote even if they are not admins.
              Because framework bundles can require runtime scripts, users listed here may commit blocked script types to this bundle. Malware scanning still applies when enabled.
            </div>
            <div style={styles.gitAccessRow}>
              <input
                style={{ ...styles.input, ...styles.gitAccessInput }}
                value={gitAccessEmail}
                onChange={(event) => setGitAccessEmail(event.target.value)}
                placeholder="user@example.com"
              />
              <button type="button" style={styles.secondaryButton} onClick={() => void addGitAccessEmail()} disabled={updatingGitAccess}>
                {updatingGitAccess ? 'Saving...' : 'Add Email'}
              </button>
            </div>
            {!form.allowedGitEmails?.length ? (
              <div style={styles.emptyState}>No non-admin users have app git access yet.</div>
            ) : (
              <div style={styles.gitAccessList}>
                {form.allowedGitEmails.map((email) => (
                  <div key={email} style={styles.gitAccessItem}>
                    <code style={styles.gitAccessCode}>{email}</code>
                    <button
                      type="button"
                      style={styles.smallDangerButton}
                      onClick={() => void removeGitAccessEmail(email)}
                      disabled={updatingGitAccess}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            {selectedItem && (releaseValue(selectedItem, 'gitAccessUpdatedAt') || releaseValue(selectedItem, 'gitAccessUpdatedBy')) ? (
              <div style={styles.gitAccessAudit}>
                Last updated by {releaseValue(selectedItem, 'gitAccessUpdatedBy') || 'unknown'}
                {releaseValue(selectedItem, 'gitAccessUpdatedAt')
                  ? ` on ${new Date(releaseValue(selectedItem, 'gitAccessUpdatedAt')).toLocaleString()}`
                  : ''}
              </div>
            ) : null}
          </div>

          <form onSubmit={handleArchiveUpload} style={styles.uploadPanel}>
            <div style={styles.panelHeader}>Git Archive Import</div>
            <label style={styles.label}>
              <span>Git Archive (ZIP)</span>
              <input
                type="file"
                accept=".zip"
                style={styles.input}
                onChange={(event) => setArchiveFile(event.target.files?.[0] || null)}
              />
            </label>
            {archiveFile ? <div style={styles.fileName}>Selected: {archiveFile.name}</div> : null}
            <button type="submit" style={styles.primaryButton} disabled={uploading || !archiveFile}>
              {uploading ? 'Uploading...' : 'Import Git Archive'}
            </button>
            <div style={styles.noteBox}>
              This matches the plugin upload model more closely: upload a git archive zip once, then continue with normal git pushes and tags.
            </div>
          </form>

          {gitRemoteUrl ? (
            <div style={styles.gitBox}>
              <div style={styles.panelHeader}>Git Remote</div>
              <div style={styles.gitRemote}>{gitRemoteUrl}</div>
              <pre style={styles.codeBlock}>{gitCommands.join('\n')}</pre>
            </div>
          ) : null}

          {selectedItem ? (
            <div style={styles.versionBox}>
              <div style={styles.panelHeader}>Published Versions</div>
              {selectedVersions.length === 0 ? (
                <div style={styles.emptyState}>No published snapshots yet. Import or upload a release archive to create one.</div>
              ) : (
                <div style={styles.versionList}>
                  {selectedVersions.map((entry, index) => {
                    const version = releaseValue(entry, 'version') || '-';
                    const tag = releaseValue(entry, 'tag') || '';
                    const commit = releaseValue(entry, 'commit') || '';
                    const uploadedAt = releaseValue(entry, 'uploadedAt');
                    const updatedBy = releaseValue(entry, 'updatedBy') || '-';
                    const downloadUrl = releaseValue(selectedItem, 'downloadUrl');
                    const versionDownloadUrl = tag
                      ? `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}tag=${encodeURIComponent(tag)}`
                      : `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}version=${encodeURIComponent(version)}`;
                    return (
                      <div key={`${tag || version}-${index}`} style={styles.versionCard}>
                        <div style={styles.versionHeaderRow}>
                          <div>
                            <div style={styles.versionTitle}>{version}</div>
                            <div style={styles.versionMeta}>Tag: {tag || '-'}</div>
                          </div>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => window.open(versionDownloadUrl, '_blank', 'noopener,noreferrer')}
                          >
                            Download This Version
                          </button>
                        </div>
                        <div style={styles.versionMeta}>Commit: {commit || '-'}</div>
                        <div style={styles.versionMeta}>Published By: {updatedBy}</div>
                        <div style={styles.versionMeta}>
                          Published: {uploadedAt ? new Date(uploadedAt).toLocaleString() : '-'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          {status ? <div style={styles.status}>{status}</div> : null}
        </section>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    flexWrap: 'wrap'
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  heading: {
    margin: 0,
    color: '#1f2937'
  },
  subtle: {
    margin: '8px 0 0',
    color: '#6b7280',
    maxWidth: '720px',
    lineHeight: 1.5
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, 360px) minmax(0, 1fr)',
    gap: '20px'
  },
  listPanel: {
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    background: '#f9fafb',
    padding: '16px'
  },
  formPanel: {
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    background: '#ffffff',
    padding: '16px'
  },
  panelHeader: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#374151',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  listItem: {
    width: '100%',
    textAlign: 'left',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '12px',
    background: '#ffffff',
    cursor: 'pointer'
  },
  listItemActive: {
    borderColor: '#667eea',
    boxShadow: '0 0 0 1px #667eea inset'
  },
  listItemTitle: {
    fontWeight: 700,
    color: '#111827',
    marginBottom: '6px'
  },
  listItemMeta: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '3px'
  },
  emptyState: {
    color: '#6b7280',
    padding: '12px 0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  uploadPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '18px',
    paddingTop: '18px',
    borderTop: '1px solid #e5e7eb'
  },
  gitAccessBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '18px',
    paddingTop: '18px',
    borderTop: '1px solid #e5e7eb'
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: '#374151',
    fontWeight: 600
  },
  input: {
    padding: '11px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px'
  },
  textarea: {
    padding: '11px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'vertical'
  },
  gitAccessRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  gitAccessInput: {
    flex: '1 1 280px'
  },
  gitAccessList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  gitAccessItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    background: '#f9fafb',
    flexWrap: 'wrap'
  },
  gitAccessCode: {
    color: '#111827',
    fontSize: '13px',
    wordBreak: 'break-all'
  },
  gitAccessAudit: {
    fontSize: '12px',
    color: '#6b7280'
  },
  noteBox: {
    padding: '12px',
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
    borderRadius: '8px',
    color: '#4338ca',
    fontSize: '13px'
  },
  formActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  primaryButton: {
    padding: '10px 14px',
    background: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },
  secondaryButton: {
    padding: '10px 14px',
    background: '#ffffff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },
  dangerButton: {
    padding: '10px 14px',
    background: '#fff1f2',
    color: '#b91c1c',
    border: '1px solid #fecdd3',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },
  smallDangerButton: {
    padding: '8px 12px',
    background: '#fff1f2',
    color: '#b91c1c',
    border: '1px solid #fecdd3',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },
  fileName: {
    color: '#4b5563',
    fontSize: '13px'
  },
  gitBox: {
    marginTop: '18px',
    paddingTop: '18px',
    borderTop: '1px solid #e5e7eb'
  },
  versionBox: {
    marginTop: '18px',
    paddingTop: '18px',
    borderTop: '1px solid #e5e7eb'
  },
  versionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  versionCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '12px',
    background: '#f9fafb'
  },
  versionHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '8px',
    flexWrap: 'wrap'
  },
  versionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#111827'
  },
  versionMeta: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '4px'
  },
  gitRemote: {
    padding: '10px 12px',
    background: '#f3f4f6',
    borderRadius: '8px',
    color: '#111827',
    fontFamily: 'monospace',
    marginBottom: '10px'
  },
  codeBlock: {
    background: '#111827',
    color: '#e5e7eb',
    padding: '14px',
    borderRadius: '8px',
    overflowX: 'auto',
    fontSize: '13px',
    lineHeight: 1.5
  },
  status: {
    marginTop: '14px',
    padding: '12px',
    borderRadius: '8px',
    background: '#f3f4f6',
    color: '#374151'
  }
};

export default AdminAppReleaseManager;
